import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import { get } from "lodash-es";
import {
	type Control,
	Controller,
	type DeepMap,
	type FieldError,
	type FieldPath,
	type FieldValues,
} from "react-hook-form";

import { ErrorMessage } from "Forms/Components/ErrorMessage";
import {
	StyledFormControl,
	StyledFormInput,
	StyledFormLabelWrapper,
} from "Forms/Components/FormStyles";
import type { CodeListItem } from "Infrastructure/Models/ICodeListDto";
import { StyledInputLabel } from "Shared/StyledComponents";

export type GroupedCodeList = {
	group: string;
	items: CodeListItem[];
};

type CodeListItemWithGroup = CodeListItem & { _group?: string };

export type BlFormAutocompleteProps<FormType extends FieldValues> = {
	codeList?: CodeListItem[];
	groupedCodeList?: GroupedCodeList[];
	name: FieldPath<FormType>;
	formControlClass?: "long" | "medium" | "tiny" | string;
	label?: string;
	labelTooltip?: string;
	control: Control<FormType, object>;
	errors: DeepMap<any, FieldError>;
	disabled?: boolean;
	placeholder?: string;
	labelEndAdornment?: React.JSX.Element;
	onChange?: (value: string | null) => void;
	loading?: boolean;
};

export const BlFormAutocomplete = <T extends object>({
	codeList,
	groupedCodeList,
	name,
	control,
	label,
	disabled,
	errors,
	formControlClass,
	placeholder,
	labelEndAdornment,
	onChange,
	loading,
}: BlFormAutocompleteProps<T>) => {
	const error: FieldError | undefined = get(errors, name);

	// Flatten grouped code list with group information attached
	const allItems: CodeListItemWithGroup[] = groupedCodeList
		? groupedCodeList.flatMap((group) =>
				group.items.map((item) => ({
					...item,
					_group: group.group,
				})),
			)
		: (codeList ?? []);

	return (
		<Controller
			control={control}
			name={name}
			render={({
				field: { onChange: controllerOnChange, value, onBlur, name: fieldName },
			}) => {
				const selectedOption =
					(value &&
						allItems.find(
							(item) => item.code === value || item.id === value,
						)) ||
					null;

				const onValueChange = (
					_event: React.SyntheticEvent,
					newValue: CodeListItemWithGroup | null,
				) => {
					const val = newValue?.code ?? newValue?.id ?? null;
					controllerOnChange(val);
					if (onChange) {
						onChange(val as string | null);
					}
				};

				return (
					<StyledFormControl
						className={`bl-form-control ${formControlClass ?? ""} ${
							!label ? "no-label" : ""
						}`}
					>
						{!!label && (
							<StyledFormLabelWrapper
								$disabled={disabled ?? false}
								className="bl-form-control__label"
							>
								<StyledInputLabel htmlFor={fieldName} $hasError={!!error}>
									<Typography>{label}</Typography>
									{!!labelEndAdornment && (
										<Box m={0.5}>{labelEndAdornment}</Box>
									)}
								</StyledInputLabel>
							</StyledFormLabelWrapper>
						)}
						<StyledFormInput className="bl-form-control__input">
							<Autocomplete
								value={selectedOption}
								onChange={onValueChange}
								onBlur={onBlur}
								options={allItems}
								groupBy={
									groupedCodeList ? (option) => option._group ?? "" : undefined
								}
								getOptionLabel={(option) => option.name}
								isOptionEqualToValue={(option, value) =>
									option.code === value.code || option.id === value.id
								}
								disabled={disabled}
								loading={loading}
								loadingText="Načítání..."
								noOptionsText="Žádné možnosti"
								renderInput={(params) => (
									<TextField
										{...params}
										placeholder={placeholder}
										error={!!error}
										sx={{
											"& .MuiOutlinedInput-root": {
												padding: "8px 12px",
												fontSize: "16px",
												fontWeight: 400,
												lineHeight: 1.4,
												color: "#233145",
												"& .MuiOutlinedInput-notchedOutline": {
													borderColor: error ? "error.main" : "#E9EAEB",
												},
												"&:hover .MuiOutlinedInput-notchedOutline": {
													borderColor: error ? "error.main" : "primary.main",
												},
												"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
													borderColor: error ? "error.main" : "primary.main",
													borderWidth: 1,
												},
												"&.Mui-disabled": {
													backgroundColor: "#F5F5F580",
													"& .MuiOutlinedInput-notchedOutline": {
														borderColor: "#E9EAEB80",
													},
												},
											},
											"& .MuiInputBase-input": {
												padding: "0 !important",
											},
										}}
									/>
								)}
								sx={{
									width: "100%",
								}}
							/>
						</StyledFormInput>
						<ErrorMessage message={error?.message} />
					</StyledFormControl>
				);
			}}
		/>
	);
};
